import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useLayoutEffect } from 'react'
import { mergeRegister } from '@lexical/utils'
import {
  $createNodeSelection,
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isDecoratorNode,
  $isNodeSelection,
  $isRangeSelection,
  $setSelection,
  KEY_ARROW_UP_COMMAND,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  COMMAND_PRIORITY_LOW,
  ParagraphNode,
  $isTextNode,
  type LexicalEditor,
  type LexicalNode,
} from 'lexical'
import { $createHeadingNode, HeadingNode, QuoteNode } from '@lexical/rich-text'
import { LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'

const SKIP_DOM_SELECTION_TAG = 'skip-dom-selection'

function isEditorFocused(editor: LexicalEditor) {
  const root = editor.getRootElement()
  if (!root) return false
  const active = document.activeElement
  return active === root || root.contains(active)
}

function normalizeBlock(node: any) {
  if (['justify', 'start', 'end'].includes(node.getFormatType?.())) {
    node.setFormat?.('left')
  }
  if (node.getIndent?.() !== 0) node.setIndent?.(0)
  if (node.getDirection?.() !== 'ltr') node.setDirection?.('ltr')
}

function normalizeHeading(node: HeadingNode) {
  normalizeBlock(node)

  const nodes = node.getChildren()
  nodes.forEach((child) => {
    if ($isTextNode(child) && child.hasFormat('bold')) {
      child.toggleFormat('bold')
    }
  })

  const tag = node.getTag()
  if (tag === 'h4' || tag === 'h5' || tag === 'h6') {
    const newNode = $createHeadingNode('h3')
    newNode.append(...node.getChildren())
    node.insertBefore(newNode)
    node.remove()
  }
}

function BehaviourPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useLayoutEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ dirtyElements, dirtyLeaves, tags }) => {
        if (tags?.has?.('behaviour')) return
        if (tags?.has?.('external-sync')) return
        if (dirtyLeaves.size === 0 && dirtyElements.size === 0) return
        if (!isEditorFocused(editor)) return

        editor.update(
          () => {
            const root = $getRoot()
            const stack: LexicalNode[] = [...root.getChildren()]

            while (stack.length) {
              const n: any = stack.pop()!

              if (n instanceof HeadingNode) normalizeHeading(n)
              else if (n instanceof ParagraphNode) normalizeBlock(n)
              else if (n instanceof QuoteNode) normalizeBlock(n)
              else if (n instanceof LinkNode) normalizeBlock(n)
              else if (n instanceof ListNode) normalizeBlock(n)
              else if (n instanceof ListItemNode) normalizeBlock(n)

              const kids = n.getChildren?.()
              if (kids && kids.length) {
                for (let i = 0; i < kids.length; i++) stack.push(kids[i])
              }
            }
          },
          { tag: ['behaviour', SKIP_DOM_SELECTION_TAG] } as any,
        )
      }),

      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()
          if (!$isNodeSelection(selection)) return false

          event.preventDefault()

          const node = selection.getNodes()[0]
          const prev = node.getPreviousSibling()
          const next = node.getNextSibling()

          if (!prev && !next) {
            const paragraph = $createParagraphNode()
            $getRoot().append(paragraph)
            paragraph.select()
          }

          node.remove()
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),

      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()
          if (!$isNodeSelection(selection)) return false

          event.preventDefault()

          const node = selection.getNodes()[0]
          const prev = node.getPreviousSibling()
          const next = node.getNextSibling()

          if (!prev && !next) {
            const paragraph = $createParagraphNode()
            $getRoot().append(paragraph)
            paragraph.select()
          }

          node.remove()
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),

      editor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()

          if ($isNodeSelection(selection)) {
            event.preventDefault()

            const currentNode = selection.getNodes()[0]
            const previousSibling = currentNode.getPreviousSibling()

            if (!previousSibling) {
              const paragraphNode = $createParagraphNode()
              currentNode.insertBefore(paragraphNode)
              paragraphNode.select()
              return true
            }

            if ($isDecoratorNode(previousSibling)) {
              const nodeSelection = $createNodeSelection()
              nodeSelection.add(previousSibling.getKey())
              $setSelection(nodeSelection)
              return true
            }

            previousSibling.selectEnd()
            return true
          }

          if ($isRangeSelection(selection) && selection.isCollapsed()) {
            const topLevelElement = selection.anchor
              .getNode()
              .getTopLevelElement()
            const prevSibling = topLevelElement?.getPreviousSibling()

            if ($isDecoratorNode(prevSibling)) {
              event.preventDefault()
              const nodeSelection = $createNodeSelection()
              nodeSelection.add(prevSibling.getKey())
              $setSelection(nodeSelection)
              return true
            }
          }

          return false
        },
        COMMAND_PRIORITY_LOW,
      ),

      editor.registerCommand(
        KEY_ARROW_DOWN_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()

          if ($isNodeSelection(selection)) {
            event.preventDefault()

            const currentNode = selection.getNodes()[0]
            const nextSibling = currentNode.getNextSibling()

            if (!nextSibling) {
              const paragraphNode = $createParagraphNode()
              currentNode.insertAfter(paragraphNode)
              paragraphNode.select()
              return true
            }

            if ($isDecoratorNode(nextSibling)) {
              const nodeSelection = $createNodeSelection()
              nodeSelection.add(nextSibling.getKey())
              $setSelection(nodeSelection)
              return true
            }

            nextSibling.selectStart()
            return true
          }

          if ($isRangeSelection(selection) && selection.isCollapsed()) {
            const topLevelElement = selection.anchor
              .getNode()
              .getTopLevelElement()
            const nextSibling = topLevelElement?.getNextSibling()

            if ($isDecoratorNode(nextSibling)) {
              event.preventDefault()
              const nodeSelection = $createNodeSelection()
              nodeSelection.add(nextSibling.getKey())
              $setSelection(nodeSelection)
              return true
            }
          }

          return false
        },
        COMMAND_PRIORITY_LOW,
      ),

      editor.registerCommand(
        KEY_ARROW_RIGHT_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()
          if (!$isNodeSelection(selection)) return false

          event.preventDefault()

          const currentNode = selection.getNodes()[0]
          const nextSibling = currentNode.getNextSibling()

          if (!nextSibling) {
            const paragraphNode = $createParagraphNode()
            currentNode.insertAfter(paragraphNode)
            paragraphNode.select()
            return true
          }

          if ($isDecoratorNode(nextSibling)) {
            const nodeSelection = $createNodeSelection()
            nodeSelection.add(nextSibling.getKey())
            $setSelection(nodeSelection)
            return true
          }

          nextSibling.selectStart()
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),

      editor.registerCommand(
        KEY_ARROW_LEFT_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()
          if (!$isNodeSelection(selection)) return false

          event.preventDefault()

          const currentNode = selection.getNodes()[0]
          const previousSibling = currentNode.getPreviousSibling()

          if (!previousSibling) {
            const paragraphNode = $createParagraphNode()
            currentNode.insertBefore(paragraphNode)
            paragraphNode.select()
            return true
          }

          if ($isDecoratorNode(previousSibling)) {
            const nodeSelection = $createNodeSelection()
            nodeSelection.add(previousSibling.getKey())
            $setSelection(nodeSelection)
            return true
          }

          previousSibling.selectEnd()
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),
    )
  }, [editor])

  return null
}

export { BehaviourPlugin }
